import { faAngleLeft, faCodeBranch, faCubes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useConfigurationContext } from '@partful/features/configurations/lib/configurationProvider';
import { useInstructionForConfiguration } from '@partful/features/instructions/root/lib/useInstructionForConfiguration';
import { ErrorMessage } from '@partful/features/instructions/ui/ErrorMessage';
import { useListInstructionVersions } from '@partful/features/instructions/versions/lib/useListInstructionVersions';
import { Button } from '@partful/shared/ui/Button';
import { WizardPageContent, WizardPageHeader, WizardPageLayout } from '@partful/shared/ui/layouts/WizardPageLayout';
import { LoadingSpinner } from '@partful/shared/ui/LoadingSpinner/LoadingSpinner';
import { Typography } from '@partful/shared/ui/Typography';
import { Fragment, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { VersionCard } from './ui/VersionCard';
import styles from './VersionsPage.module.scss';

export function VersionsPage() {
  const { t: i18n } = useTranslation();
  const configuration = useConfigurationContext();
  const {
    data: instruction,
    isLoading: isInstructionLoading,
    isError: isInstructionError,
  } = useInstructionForConfiguration(configuration);

  /**
   * Fetch versions for the given instruction (versions are infinitely paged)
   */
  const {
    isError: isVersionsError,
    isLoading: isVersionsLoading,
    isFetching,
    data: versions,
    hasMore,
    fetchMore,
  } = useListInstructionVersions(instruction?.id);

  /**
   * Simplified states for rendering
   */
  const isError = isInstructionError || isVersionsError;
  const isLoading = !isError && (isInstructionLoading || isVersionsLoading);

  /**
   * Sort published versions to the top then descending version order
   */
  /* istanbul ignore next - no need to test sort algorithm so much */
  const sortedVersions = useMemo(
    () =>
      versions?.slice().sort((a, b) => {
        if (a.status !== b.status) {
          if (a.status === 'published') return -1;
          if (b.status === 'published') return 1;
        }
        return b.versionNumber - a.versionNumber;
      }),
    [versions]
  );

  return (
    <WizardPageLayout>
      <WizardPageHeader>
        <div>
          <Button linkComponent={Link} to="/instructions">
            <FontAwesomeIcon icon={faAngleLeft} />
            {i18n('common:backButton')}
          </Button>
        </div>
        <Typography appearance="heading5" as="h1">
          <FontAwesomeIcon icon={faCubes} /> {configuration.name}
        </Typography>
        <Typography appearance="heading6" as="h2" data-testid="versions-title">
          {i18n('instructions:manage.versions.title')}
        </Typography>
        <Typography as="p">
          <Trans i18nKey="instructions:manage.versions.description" components={{ em: <em /> }} />
        </Typography>
      </WizardPageHeader>
      <WizardPageContent style={{ position: 'relative' }}>
        {isError && (
          <ErrorMessage
            icon={<FontAwesomeIcon icon={faCodeBranch} />}
            title={i18n('instructions:manage.versions.loadFailed.title')}
            message={i18n('instructions:manage.versions.loadFailed.message')}
          />
        )}
        {isLoading && <LoadingSpinner className={styles.loading} />}
        {sortedVersions && (
          <div className={styles.versions}>
            {sortedVersions.map((version, index) => (
              <Fragment key={version.id}>
                {index === 1 && <hr className={styles.divider} />}
                <VersionCard version={version} data-testid={`version-card-${version.versionNumber}`} />
              </Fragment>
            ))}
            {!isFetching && hasMore && (
              <Button className={styles.moreButton} onClick={() => fetchMore()} data-testid="more-button">
                {i18n('instructions:manage.versions.seeMore')}
              </Button>
            )}
            {isFetching && hasMore && <LoadingSpinner className={styles.loading} data-testid="more-loading" />}
          </div>
        )}
      </WizardPageContent>
    </WizardPageLayout>
  );
}
